import { useDispatch, useSelector } from 'react-redux'

import { ICC_REGEX } from '@masfront/common-b2c'
import { validateSchema } from '@masfront/common-forms'
import { useFormikContext, setIn, getIn } from 'formik'
import { string, object } from 'yup'

import { REQUIRED_ICC_ERROR, INVALID_ICC } from '../../constants/errors'
import { UseICCField } from '../../model/line.model'
import { setIccBalance } from '../../store/line.reducer'
import { selectLineIccBalance, selectLineIccAvailable } from '../../store/line.selectors'

import { selectDealer, selectIsOffline } from '~/agent/store/agent.selectors'
import { SignUp } from '~/basket/model/lines/signup.model'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import { RootState } from '~/core/store/root.reducer'
import { selectIsPostpaid } from '~/sale/store/sale.selectors'

export default function useICCField(id: string): UseICCField {
  const { errors, setStatus, values, status } = useFormikContext<SignUp>()

  const dispatch = useDispatch()
  const { lineService } = useAppDepsContext()
  const isOffline = useSelector(selectIsOffline)
  const dealer = useSelector(selectDealer)
  const icc = getIn(values, 'icc')
  const iccBalance = useSelector((state: RootState) => selectLineIccBalance(state, id))
  const iccAvailable = useSelector((state: RootState) => selectLineIccAvailable(state, id))
  const isPostpaid = useSelector(selectIsPostpaid)

  const iccSchema = object().shape({
    icc: string()
      .label('ICC en Tienda')
      .test('equal-icc', 'El ICC en tienda no puede ser igual que el ICC', function (val) {
        if (!isOffline) return true
        const ctx = this.options.context as { [key: string]: string }
        return val !== ctx.donorICC
      })
      .test('registered-icc', 'La SIM no está registrada', function (val) {
        // have to repeat previous test because it is not sequential.
        // See https://github.com/jquense/yup/issues/851

        if (!isOffline) return true
        const ctx = this.options.context as { [key: string]: string }

        if (val !== ctx.donorICC) {
          setStatus(setIn(status, `icc.loading`, true))
          return lineService
            .fetchICCValidation(val, dealer)
            .then((res: any) => {
              setStatus(setIn(status, `icc.loading`, false))
              dispatch(
                setIccBalance({ lineId: id, balance: res?.balance, available: res?.available }),
              )
              if (!res?.available) {
                return false
              }
              return true
            })
            .catch(() => {
              setStatus(setIn(status, `icc.loading`, false))
              return false
            })
        }
        return true
      }),
  })

  async function validateICC(value: string) {
    if (!isOffline || !dealer) return ''
    if (!value) {
      return REQUIRED_ICC_ERROR
    }
    if (!ICC_REGEX.test(value)) {
      return INVALID_ICC
    }

    if (value === icc) return errors.icc

    const isSameValue = await validateSchema({ icc: value }, iccSchema, values)

    return isSameValue.icc
  }

  return { validateICC, iccBalance, iccAvailable, isPostpaid }
}
